/**
 * ERP Schedule Data Age card — Import Monitor overview (Avanti tenants only).
 *
 * The ERP only rewrites schedule content at its weekly regeneration, so the
 * portal's schedule photograph can be up to a week behind the planners'
 * screens while every nightly push completes green. This card shows the age
 * of the photograph so staleness announces itself instead of surfacing as
 * supplier queries. Rendered only when the data-age endpoint reports
 * `enabled` — every non-Avanti tenant sees nothing.
 */
import { useEffect, useRef, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { CalendarClock, ChevronLeft, ChevronRight, Loader2, Search } from 'lucide-react';
import { importsAPI } from '../../lib/api/imports';
import type { DataAgeResponse, DataAgeChange } from '../../lib/api/imports';

const CHANGE_BADGE: Record<DataAgeChange['type'], { label: string; className: string }> = {
  added: { label: 'Added', className: 'bg-green-50 text-green-700 border-green-300' },
  removed: { label: 'Removed', className: 'bg-red-50 text-red-700 border-red-300' },
  reactivated: { label: 'Reactivated', className: 'bg-blue-50 text-blue-700 border-blue-300' },
  qty_changed: { label: 'Qty changed', className: 'bg-amber-50 text-amber-700 border-amber-300' },
};

interface DataAgeCardProps {
  data: DataAgeResponse | null;
}

function ageDays(iso: string): number {
  return Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000);
}

function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return 'never';
  return new Date(iso).toLocaleString(undefined, {
    weekday: 'short', day: '2-digit', month: 'short',
    hour: '2-digit', minute: '2-digit',
  });
}

const PAGE_SIZE = 25;

export function DataAgeCard({ data: initial }: DataAgeCardProps) {
  const [data, setData] = useState<DataAgeResponse | null>(initial);
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [busy, setBusy] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // The dashboard's 30s auto-refresh keeps feeding us fresh page-1 data;
  // accept it only while the user hasn't paged or filtered.
  useEffect(() => {
    if (page === 1 && search === '') setData(initial);
  }, [initial, page, search]);

  useEffect(() => {
    if (page === 1 && search === '') return; // covered by the prop
    let cancelled = false;
    setBusy(true);
    importsAPI.getDataAge({ page, page_size: PAGE_SIZE, search: search || undefined })
      .then(res => { if (!cancelled) setData(res); })
      .catch(() => {})
      .finally(() => { if (!cancelled) setBusy(false); });
    return () => { cancelled = true; };
  }, [page, search]);

  const handleSearch = (value: string) => {
    setSearchInput(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => { setSearch(value.trim()); setPage(1); }, 400);
  };

  if (!data?.enabled) return null;

  const total = data.changes_total ?? data.changes?.length ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const days = data.last_replan_at ? ageDays(data.last_replan_at) : null;
  const status = days === null
    ? { label: 'Unknown', className: 'bg-gray-100 text-gray-700 border-gray-300' }
    : days <= 2
      ? { label: 'Current', className: 'bg-green-50 text-green-700 border-green-300' }
      : days <= 6
        ? { label: `${days} days old`, className: 'bg-amber-50 text-amber-700 border-amber-300' }
        : { label: `Stale — ${days} days old`, className: 'bg-red-50 text-red-700 border-red-300' };

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center justify-between">
          <span className="flex items-center gap-2">
            <CalendarClock className="h-4 w-4" />
            ERP Schedule Data Age
          </span>
          <span className={`text-xs font-semibold px-2.5 py-1 rounded-full border ${status.className}`}>
            {status.label}
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        <div className="flex items-baseline justify-between text-sm">
          <span className="text-muted-foreground">Last schedule re-plan received</span>
          <span className="font-medium">
            {formatDateTime(data.last_replan_at)}
            {data.replan_changes_on_last_day
              ? <span className="text-xs text-muted-foreground ml-1.5">({data.replan_changes_on_last_day.toLocaleString()} schedule changes)</span>
              : null}
          </span>
        </div>
        {data.push_state === 'complete' && data.push_meta ? (
          <div className="flex items-baseline justify-between text-sm">
            <span className="text-muted-foreground">Last complete push</span>
            <span className="font-medium">
              {formatDateTime(data.push_meta.completed_at)}
              <span className="text-xs text-muted-foreground ml-1.5">
                {data.push_meta.pages ? `${data.push_meta.pages} pages · ` : ''}{data.push_meta.rows?.toLocaleString() ?? '?'} rows
                {data.push_meta.rows_processed != null && data.push_meta.rows != null
                  && data.push_meta.rows_processed < data.push_meta.rows
                  ? ` · only ${data.push_meta.rows_processed.toLocaleString()} processed`
                  : ''}
              </span>
            </span>
          </div>
        ) : data.push_state === 'in_progress' ? (
          <div className="flex items-baseline justify-between text-sm">
            <span className="text-muted-foreground">Push in progress</span>
            <span className="font-medium">
              started {formatDateTime(data.push_started_at)}
              <span className="text-xs text-muted-foreground ml-1.5">
                {data.push_meta?.rows_processed?.toLocaleString() ?? 0} rows processed so far
              </span>
            </span>
          </div>
        ) : (
          <div className="flex items-baseline justify-between text-sm">
            <span className="text-muted-foreground">Last PO push activity (job-level)</span>
            <span className="font-medium">{formatDateTime(data.last_po_push_at)}</span>
          </div>
        )}
        <p className="text-xs text-muted-foreground pt-1">
          Pushes arrive nightly, but the ERP only rewrites schedule content at its
          weekly regeneration — quantities and dates changed in the ERP mid-week
          reach the portal at the next regeneration.
        </p>

        {((data.changes && data.changes.length > 0) || search !== '' || page > 1) && (
          <div className="pt-3">
            <div className="flex items-center justify-between gap-3 mb-2">
              <h4 className="text-sm font-semibold shrink-0">
                {data.changes_scope === 'last_push'
                  ? 'What changed in the last push'
                  : 'What changed in the last re-plan'}
              </h4>
              <div className="relative flex-1 max-w-xs">
                <Search className="h-3.5 w-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <input
                  value={searchInput}
                  onChange={e => handleSearch(e.target.value)}
                  placeholder="Filter by PO or part number"
                  className="w-full h-8 pl-8 pr-2 text-sm border rounded-md bg-background"
                />
              </div>
              <span className="text-xs text-muted-foreground shrink-0">
                {busy
                  ? <Loader2 className="h-3.5 w-3.5 animate-spin inline" />
                  : `${total.toLocaleString()} change${total === 1 ? '' : 's'}${search ? ' (filtered)' : ''}`}
              </span>
            </div>
            <div className="overflow-x-auto max-h-96 overflow-y-auto border rounded-md">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-muted/80 backdrop-blur">
                  <tr className="text-left text-muted-foreground">
                    <th className="py-1.5 px-3 font-medium">Change</th>
                    <th className="py-1.5 px-3 font-medium">PO</th>
                    <th className="py-1.5 px-3 font-medium text-right">Line</th>
                    <th className="py-1.5 px-3 font-medium">Part</th>
                    <th className="py-1.5 px-3 font-medium">Schedule date</th>
                    <th className="py-1.5 px-3 font-medium text-right">Qty</th>
                    <th className="py-1.5 px-3 font-medium">Received at</th>
                  </tr>
                </thead>
                <tbody>
                  {(data.changes ?? []).map((c, i) => {
                    const badge = CHANGE_BADGE[c.type];
                    return (
                      <tr key={i} className="border-t">
                        <td className="py-1.5 px-3">
                          <span className={`text-xs font-semibold px-2 py-0.5 rounded-full border ${badge.className}`}>
                            {badge.label}
                          </span>
                        </td>
                        <td className="py-1.5 px-3 font-mono text-xs">{c.po_number}</td>
                        <td className="py-1.5 px-3 text-right tabular-nums">{c.line_number ?? '—'}</td>
                        <td className="py-1.5 px-3 font-mono text-xs">{c.part_number}</td>
                        <td className="py-1.5 px-3 whitespace-nowrap">
                          {c.schedule_date ? new Date(c.schedule_date).toLocaleDateString() : '—'}
                        </td>
                        <td className="py-1.5 px-3 text-right tabular-nums whitespace-nowrap">
                          {c.type === 'qty_changed' && c.old_qty != null && c.new_qty != null
                            ? <>
                                <span className="text-muted-foreground line-through mr-1">{Number(c.old_qty).toLocaleString()}</span>
                                <span className="font-semibold">{Number(c.new_qty).toLocaleString()}</span>
                              </>
                            : c.qty != null ? Number(c.qty).toLocaleString() : '—'}
                        </td>
                        <td className="py-1.5 px-3 whitespace-nowrap text-xs text-muted-foreground">
                          {formatDateTime(c.at)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {(!data.changes || data.changes.length === 0) && (
                <p className="text-sm text-muted-foreground text-center py-6">
                  No changes match{search ? ` “${search}”` : ''}.
                </p>
              )}
            </div>
            {totalPages > 1 && (
              <div className="flex items-center justify-between mt-2 text-sm">
                <span className="text-xs text-muted-foreground">
                  Page {page} of {totalPages.toLocaleString()}
                </span>
                <div className="flex gap-1">
                  <button
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page <= 1 || busy}
                    className="h-7 px-2 border rounded-md disabled:opacity-40 hover:bg-accent flex items-center"
                    aria-label="Previous page"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    disabled={page >= totalPages || busy}
                    className="h-7 px-2 border rounded-md disabled:opacity-40 hover:bg-accent flex items-center"
                    aria-label="Next page"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
